import { useEffect, useMemo, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import { Tooltip } from "primereact/tooltip";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useNavigate, useLocation, useBlocker, useParams } from "react-router-dom";
import SaleService from "../../services/SaleService";
import SupplierService from "../../services/SupplierService";
import ProductService from "../../services/ProductService";
import { customerService } from "../../services/CustomerService";
import FinancialAccountService from "../../services/FinancialAccountService";
import WarehouseService from "../../services/WarehouseService";
import { useAuth } from "../../context/AuthContext";
import SaleReceipt from "../../components/Sale/SaleReceipt";

export default function SalesCreatePage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();
  const toast = useRef(null);
  const { user: authUser } = useAuth();

  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaleId, setLastSaleId] = useState(null);
  const receiptRef = useRef(null);

  // Refs for focus management
  const customerInputRef = useRef(null);
  const productInputRef = useRef(null);
  const quantityRef = useRef(null);
  const priceRef = useRef(null);
  const vatRef = useRef(null);
  const noteInputRef = useRef(null);
  const addLineBtnRef = useRef(null);
  const warehouseRef = useRef(null);

  const safeNumber = (val, fallback = 0) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  };

  // States
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [customerHighlightIndex, setCustomerHighlightIndex] = useState(0);

  const [productQuery, setProductQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [showList, setShowList] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [isAddFocused, setIsAddFocused] = useState(false);

  const [salesHeader, setSalesHeader] = useState({
    salesDate: new Date(),
    description: "",
  });

  const [receivableType, setReceivableType] = useState(1); // Default to Cash (Peşin)
  const [dueDate, setDueDate] = useState(null);

  const receivableTypeOptions = [
    { label: "Peşin tahsil edildi", value: 1 },
    { label: "Açık hesaba ekle", value: 2 },
    { label: "Vadeli satış", value: 3 },
  ];

  const updateSalesHeader = (updater) => {
    setSalesHeader(updater);
    setIsDirty(true);
  };

  const [lines, setLines] = useState([]);
  const [generalDiscount, setGeneralDiscount] = useState(0);

  const updateGeneralDiscount = (val) => {
    setGeneralDiscount(val);
    setIsDirty(true);
  };
  const [newLine, setNewLine] = useState({
    product: null,
    quantity: 1,
    unitPrice: 0,
    vatRate: 0,
    unit: "",
    note: "",
    warehouseId: null,
    dropFromStock: true,
    priceAlternatives: [],
    priceTierId: null,
    originalResolvedPrice: 0,
  });

  const [warehouses, setWarehouses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentType, setPaymentType] = useState("Nakit");
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId);
    if (paymentType === "SanalPos") {
      const selectedAcc = financialAccounts.find(a => a.id === accountId);
      if (selectedAcc && selectedAcc.supplierId) {
        setSelectedSupplierId(selectedAcc.supplierId);
      } else {
        setSelectedSupplierId(null);
      }
    }
  };

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplierId(supplierId);
    if (paymentType === "SanalPos") {
      const associatedAccount = financialAccounts.find(
        a => a.accountType === 5 && a.supplierId === supplierId
      );
      if (associatedAccount) {
        setSelectedAccountId(associatedAccount.id);
      } else {
        setSelectedAccountId(null);
      }
    }
  };

  const filteredAccounts = useMemo(() => {
    if (paymentType === "Nakit") return financialAccounts.filter(a => a.accountType === 1);
    if (paymentType === "Havale" || paymentType === "EFT") return financialAccounts.filter(a => a.accountType === 2);
    if (paymentType === "KrediKarti") return financialAccounts.filter(a => a.accountType === 4);
    if (paymentType === "SanalPos") return financialAccounts.filter(a => a.accountType === 5);
    return [];
  }, [financialAccounts, paymentType]);

  useEffect(() => {
    if (filteredAccounts.length === 1) {
      setSelectedAccountId(filteredAccounts[0].id);
      if (paymentType === "SanalPos" && filteredAccounts[0].supplierId) {
        setSelectedSupplierId(filteredAccounts[0].supplierId);
      }
    } else {
      setSelectedAccountId(null);
      if (paymentType === "SanalPos") {
        setSelectedSupplierId(null);
      }
    }
  }, [filteredAccounts, paymentType]);

  useEffect(() => {
    if (paymentType !== "SanalPos") {
      setSelectedSupplierId(null);
    }
  }, [paymentType]);

  const [chequeDetails, setChequeDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: ""
  });

  const [promissoryDetails, setPromissoryDetails] = useState({
    dueDate: null,
    drawer: "",
    guarantor: ""
  });

  const [createProductModal, setCreateProductModal] = useState({
    visible: false,
    productName: "",
    unit: 1,
    unitType: "Adet",
    salePrice: 0,
  });

  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", code: "", phone: "" });

  const [priceModal, setPriceModal] = useState({
    visible: false,
    product: null,
    salePrice: 0,
    salePriceIsVatIncluded: true,
    taxRate: 20
  });

  const [modalErrors, setModalErrors] = useState({});
  const [isUnitTypeOpen, setIsUnitTypeOpen] = useState(false);
  const unitInputRef = useRef(null);
  const productNameRef = useRef(null);
  const saveProductBtnRef = useRef(null);

  const paymentTypeOptions = [
    { label: "Nakit", value: "Nakit" },
    { label: "Kredi Kartı", value: "KrediKarti" },
    { label: "Havale / EFT", value: "Havale" },
    { label: "Sanal POS", value: "SanalPos" },
    { label: "Çek", value: "Çek" },
    { label: "Senet", value: "Senet" },
  ];

  const unitTypeOptions = [
    { label: 'Litre', value: 'Litre' },
    { label: 'Mililitre', value: 'Mililitre' },
    { label: 'Kilogram', value: 'Kg' },
    { label: 'Gram', value: 'Gram' },
    { label: 'Adet', value: 'Adet' },
    { label: 'Metre', value: 'Metre' }
  ];

  // Helper for InputNumber focus/select
  const focusAndSelect = (ref) => {
    if (!ref.current) return;
    const inputEl = ref.current.getInput ? ref.current.getInput() : ref.current.input?.current || ref.current.input;
    if (inputEl && inputEl.select) {
      inputEl.focus();
      inputEl.select();
    } else {
      ref.current.focus?.();
    }
  };

  // Effects
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    FinancialAccountService.getAll().then(res => {
      if (res.success) {
        setFinancialAccounts(res.data || []);
      }
    });
    WarehouseService.getAll().then(res => {
      if (res.success) {
        setWarehouses(res.data || []);
      }
    });
    SupplierService.getAllSuppliers().then(data => {
      setSuppliers(data || []);
    });
  }, []);

  useEffect(() => {
    if (editId) {
      SaleService.getSaleDetail(editId).then(res => {
        if (res) {
          setSelectedCustomer({
            id: res.customerId,
            name: res.customerName,
            balance: res.customerBalance
          });
          setCustomerQuery(res.customerName);
          setSalesHeader({
            salesDate: new Date(res.saleDate),
            description: res.saleNote || "",
            saleNo: res.saleNo
          });
          setGeneralDiscount(res.discountTotal || 0);
          if (res.customerId) {
            setReceivableType(res.customerReceivableType || 2);
            setDueDate(res.dueDate ? new Date(res.dueDate) : null);
          } else {
            setReceivableType(1);
            setDueDate(null);
          }
          const loadLinesAlternatives = async (items, customerId) => {
            const mappedLines = await Promise.all(items.map(async (item, idx) => {
              let priceAlternatives = [];
              let priceTierId = item.priceTierId;
              try {
                const priceRes = await ProductService.getPrice(item.productId, customerId);
                if (priceRes.data) {
                  priceAlternatives = priceRes.data.priceAlternatives || [];
                }
              } catch (err) {
                console.error("Fiyat detayı alınamadı", err);
              }
              return {
                id: `line-${idx}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                productId: item.productId,
                supplierId: item.supplierId,
                productName: item.productName,
                quantity: item.quantity,
                unitPrice: item.unitPrice,
                vatRate: item.vatRate || 0,
                unit: "",
                note: item.note,
                warehouseId: item.warehouseId,
                warehouseName: item.warehouseName,
                dropFromStock: item.dropFromStock !== false,
                priceAlternatives: priceAlternatives,
                priceTierId: priceTierId,
                originalPrice: item.unitPrice
              };
            }));
            setLines(mappedLines);
          };
          loadLinesAlternatives(res.items, res.customerId);
          setPayments(res.payments.map(p => {
            return {
              id: p.id,
              amount: p.amount,
              method: p.method,
              note: p.description,
              financialAccountId: p.financialAccountId,
              supplierId: p.supplierId,
              date: new Date()
            };
          }));
          setIsDirty(false);
        }
      });
    } else if (location.state?.preSelectedCustomer) {
      selectCustomer(location.state.preSelectedCustomer);
    } else if (location.state?.preSelectedSupplier) {
      selectCustomer({
        id: 0,
        name: location.state.preSelectedSupplier.name,
        phone: location.state.preSelectedSupplier.phone,
        supplierId: location.state.preSelectedSupplier.id,
        balance: 0
      });
    } else {
      setTimeout(() => {
        customerInputRef.current?.focus();
      }, 0);
    }
  }, [location.state, editId]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );
  const formatDateOnly = (date) => {
    if (!date) return null;

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  // Keyboard shortcut F2
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (createProductModal.visible || showCustomerDialog || blocker.state === "blocked" || lastSaleId) return;
      if (e.key === "F2") {
        e.preventDefault();
        if (lines.length > 0) handleSave();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lines, createProductModal.visible, showCustomerDialog, blocker.state, lastSaleId]);

  // Refs to avoid stale state in keyboard shortcuts
  const newLineRef = useRef(newLine);
  const selectedCustomerRef = useRef(selectedCustomer);

  useEffect(() => {
    newLineRef.current = newLine;
  }, [newLine]);

  useEffect(() => {
    selectedCustomerRef.current = selectedCustomer;
  }, [selectedCustomer]);

  // Keyboard shortcut Alt + [1-9] for pricing tier selection
  useEffect(() => {
    const handlePriceShortcut = (e) => {
      const currentNewLine = newLineRef.current;
      if (!currentNewLine || !currentNewLine.product) return;

      if (e.altKey && e.key >= "1" && e.key <= "9") {
        const index = parseInt(e.key) - 1;

        if (index === 0) {
          e.preventDefault();
          const targetPrice = currentNewLine.originalResolvedPrice || 0;
          const targetTierId = selectedCustomerRef.current?.defaultPriceTierId || null;

          setNewLine((prev) => ({
            ...prev,
            unitPrice: targetPrice,
            priceTierId: targetTierId
          }));

          toast.current?.show({
            severity: "info",
            summary: "Fiyat Değiştirildi",
            detail: `Standart Fiyat: ${targetPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
            life: 1500
          });
        } else {
          const altIdx = index - 1;
          if (currentNewLine.priceAlternatives && currentNewLine.priceAlternatives[altIdx]) {
            e.preventDefault();
            const altPriceObj = currentNewLine.priceAlternatives[altIdx];

            setNewLine((prev) => ({
              ...prev,
              unitPrice: altPriceObj.priceInclVat,
              priceTierId: altPriceObj.priceTierId
            }));

            toast.current?.show({
              severity: "info",
              summary: "Fiyat Değiştirildi",
              detail: `${altPriceObj.priceTierName}: ${altPriceObj.priceInclVat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
              life: 1500
            });
          }
        }
      }
    };

    window.addEventListener("keydown", handlePriceShortcut);
    return () => window.removeEventListener("keydown", handlePriceShortcut);
  }, []);


  // Customer Logic
  const searchCustomers = async (query) => {
    try {
      const list = await SaleService.searchCustomer({ params: { query, take: 20 } });
      setCustomers(list || []);
      setShowCustomerList((list || []).length > 0);
      setCustomerHighlightIndex(0);
    } catch (err) {
      setCustomers([]);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery(customer?.name || "");
    setCustomers([]);
    setShowCustomerList(false);
    setIsDirty(true);

    if (customer) {
      // Only auto-switch to Open Account if it's currently Cash and not in Edit Mode
      // This prevents overwriting the loaded value when editId is present
      if (receivableType === 1 && !editId) setReceivableType(2);
    } else {
      setReceivableType(1); // Lock to Cash for retail
      setDueDate(null);
    }

    setTimeout(() => productInputRef.current?.focus(), 100);
  };

  // Product Logic
  const searchProducts = async (query) => {
    try {
      const res = await SaleService.searchProduct({
        params: {
          query,
          take: 20,
          customerId: selectedCustomer?.id || null
        }
      });
      const list = enrichProducts(res || [], query);
      setProducts(list);
      setHighlightIndex(0);
      setShowList(true);
    } catch (err) {
      setProducts([]);
    }
  };

  const enrichProducts = (list, query) => {
    if (!query) return list;
    const exists = list.some((p) => (p.name || "").toLowerCase() === query.toLowerCase());
    if (!exists && query.length >= 3) {
      return [
        ...list
      ];
    }
    return list;
  };

  const selectProduct = async (product) => {
    if (!product) return;

    let resolvedPrice = product.salePrice || 0;
    let priceAlternatives = [];
    try {
      const priceRes = await ProductService.getPrice(product.id || product.Id, selectedCustomer?.id || null);
      if (priceRes.data) {
        resolvedPrice = priceRes.data.salePrice;
        priceAlternatives = priceRes.data.priceAlternatives || [];
      }
    } catch (err) {
      console.error("Fiyat detayı alınamadı", err);
    }

    if (resolvedPrice <= 0) {
      confirmDialog({
        message: 'Ürünün satış fiyatı yok, belirlemek ister misiniz?',
        header: 'Fiyat Belirle',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Evet',
        rejectLabel: 'Hayır',
        acceptClassName: 'p-button-success',
        rejectClassName: 'p-button-secondary p-button-text',
        accept: () => {
          setPriceModal({
            visible: true,
            product,
            salePrice: resolvedPrice || 0,
            salePriceIsVatIncluded: product.salePriceIsVatIncluded ?? true,
            taxRate: product.vatRate || 20,
            loading: false
          });
        }
      });
      return;
    }

    setNewLine((prev) => {
      const currentWhId = prev.warehouseId;
      let targetWhId = currentWhId;

      // If product has multiple stocks, check if current WhId is among them
      if (product.warehouseStocks?.length > 1) {
        const existsInCurrent = product.warehouseStocks.some(ws => ws.warehouseId === currentWhId);
        if (!existsInCurrent) targetWhId = null; // Reset if not in current
      } else if (product.warehouseStocks?.length === 1) {
        targetWhId = product.warehouseStocks[0].warehouseId;
      } else {
        // No specific warehouse stock info, fallback to current or default
        targetWhId = currentWhId || (warehouses.find(w => w.isDefault)?.id || warehouses[0]?.id);
      }

      return {
        ...prev,
        product,
        unit: product.unit || "",
        unitPrice: resolvedPrice,
        vatRate: product.vatRate || 0,
        warehouseId: targetWhId,
        quantity: 1,
        priceAlternatives: priceAlternatives,
        priceTierId: selectedCustomer?.defaultPriceTierId || null,
        originalResolvedPrice: resolvedPrice
      };
    });
    setProductQuery(product.name || "");
    setProducts([]);
    setShowList(false);

    setTimeout(() => {
      setNewLine(latest => {
        if (!latest.warehouseId && latest.dropFromStock) {
          warehouseRef.current?.focus();
          warehouseRef.current?.show();
        } else {
          focusAndSelect(quantityRef);
        }
        return latest;
      });
    }, 400);
  };

  const handlePriceModalSave = async () => {
    try {
      const productId = priceModal.product?.id || priceModal.product?.Id;
      if (!productId) {
        toast.current.show({ severity: "error", summary: "Hata", detail: "Ürün ID'si bulunamadı" });
        return;
      }

      const res = await ProductService.getById(productId);
      const resData = res.data;

      if (resData.success) {
        const product = resData.data;
        const productDto = {
          Id: product.id || product.Id,
          Name: product.name || product.Name,
          Description: product.description || product.Description,
          UnitTypeId: product.unitTypeId || product.UnitTypeId,
          Unit: product.unit || product.Unit,
          BrandName: product.brandName || product.BrandName,
          Barcode: product.barcode || product.Barcode,
          Tags: product.tags || product.Tags,
          CriticalStockQuantity: product.criticalStockQuantity || product.CriticalStockQuantity,
          CurrentStockQuantity: product.currentStockQuantity || product.CurrentStockQuantity,
          CategoryId: product.categoryId || product.CategoryId,
          SalePrice: priceModal.salePrice,
          SalePriceIsVatIncluded: priceModal.salePriceIsVatIncluded,
          TaxRate: priceModal.taxRate,
          RecordStatus: product.recordStatus ?? product.RecordStatus
        };

        const updateRes = await ProductService.update({ ProductDto: productDto });
        const updateResData = updateRes.data;

        if (updateResData.success) {
          toast.current.show({ severity: "success", summary: "Başarılı", detail: "Ürün fiyatı güncellendi" });

          const updatedProduct = {
            ...priceModal.product,
            salePrice: priceModal.salePrice,
            salePriceIsVatIncluded: priceModal.salePriceIsVatIncluded,
            vatRate: priceModal.taxRate
          };

          setNewLine((prev) => {
            const currentWhId = prev.warehouseId;
            let targetWhId = currentWhId;

            if (updatedProduct.warehouseStocks?.length > 1) {
              const existsInCurrent = updatedProduct.warehouseStocks.some(ws => ws.warehouseId === currentWhId);
              if (!existsInCurrent) targetWhId = null;
            } else if (updatedProduct.warehouseStocks?.length === 1) {
              targetWhId = updatedProduct.warehouseStocks[0].warehouseId;
            } else {
              targetWhId = currentWhId || (warehouses.find(w => w.isDefault)?.id || warehouses[0]?.id);
            }

            return {
              ...prev,
              product: updatedProduct,
              unit: updatedProduct.unit || "",
              unitPrice: updatedProduct.salePrice || 0,
              vatRate: updatedProduct.vatRate || 0,
              warehouseId: targetWhId,
              quantity: 1,
            };
          });
          setProductQuery(updatedProduct.name || "");
          setProducts([]);
          setShowList(false);
          setPriceModal({ ...priceModal, visible: false });

          setTimeout(() => {
            setNewLine(latest => {
              if (!latest.warehouseId && latest.dropFromStock) {
                warehouseRef.current?.focus();
                warehouseRef.current?.show();
              } else {
                focusAndSelect(quantityRef);
              }
              return latest;
            });
          }, 400);
        } else {
          toast.current.show({ severity: "error", summary: "Hata", detail: updateResData.message || "Fiyat güncellenirken bir hata oluştu." });
        }
      }
    } catch (error) {
      toast.current.show({ severity: "error", summary: "Hata", detail: "Fiyat güncellenirken hata oluştu" });
    }
  };

  // Line Logic
  const handleAddLine = () => {
    if (!newLine.product || newLine.quantity <= 0) return;

    if (newLine.dropFromStock && !newLine.warehouseId) {
      toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Lütfen bir depo seçiniz." });
      warehouseRef.current?.focus();
      setTimeout(() => warehouseRef.current?.show(), 100);
      return;
    }

    const newId = Date.now();
    setLines((prev) => [
      {
        id: newId,
        productId: newLine.product.id,
        productName: newLine.product.name,
        product: newLine.product,
        lastPurchasePrice: newLine.product.lastPurchasePrice,
        quantity: newLine.quantity,
        unitPrice: newLine.unitPrice,
        vatRate: newLine.vatRate,
        unit: newLine.unit || newLine.product.unit,
        note: newLine.note,
        warehouseId: newLine.warehouseId,
        warehouseName: warehouses.find(w => w.id === newLine.warehouseId)?.name,
        dropFromStock: newLine.dropFromStock,
        supplierId: newLine.product.supplierId || 0,
        priceAlternatives: newLine.priceAlternatives || [],
        priceTierId: newLine.priceTierId,
      },
      ...prev,
    ]);

    setNewLine((prev) => ({
      product: null,
      quantity: 1,
      unitPrice: 0,
      vatRate: 0,
      unit: "",
      note: "",
      warehouseId: prev.warehouseId,
      dropFromStock: true,
      priceAlternatives: [],
      priceTierId: null,
    }));
    setIsAddFocused(false)
    setProductQuery("");
    setShowList(false);
    setIsDirty(true);
    setTimeout(() => productInputRef.current?.focus(), 0);
  };

  const changeLineQuantity = (id, delta) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? { ...l, quantity: Math.max(1, (Number(l.quantity) || 0) + delta) }
          : l
      )
    );
    setIsDirty(true);
  };

  const removeLine = (id) => {
    setLines((prev) => prev.filter((l) => l.id !== id));
    setIsDirty(true);
  };

  // Cell Editing
  const onCellEditComplete = (e) => {
    const { rowData, newValue, field } = e;
    setLines((prev) =>
      prev.map((row) =>
        row.id === rowData.id
          ? {
            ...row,
            [field]: newValue,
            ...(field === "unitPrice" ? { priceTierId: null } : {})
          }
          : row
      )
    );
    setIsDirty(true);
  };

  // Totals
  const totals = useMemo(() => {
    let gross = 0;
    let totalQuantity = 0;

    lines.forEach((l) => {
      const qty = Number(l.quantity || 0);
      const price = Number(l.unitPrice || 0);
      const lineGross = qty * price;

      gross += lineGross;
      totalQuantity += qty;
    });

    const disc = Number(generalDiscount || 0);
    const netTotal = gross - disc;

    return { gross, discountTotal: disc, netTotal, totalQuantity, lineCount: lines.length };
  }, [lines, generalDiscount]);

  const paidTotal = useMemo(() => payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0), [payments]);
  const remainingAmount = Math.max(totals.netTotal - paidTotal, 0);

  const paymentStatus = useMemo(() => {
    if (paidTotal === 0) return "UNPAID";
    if (remainingAmount > 0) return "PARTIAL";
    return "PAID";
  }, [paidTotal, remainingAmount]);

  // Payment Logic
  const handleAddPayment = () => {
    if (!paymentAmount || paymentAmount <= 0) return;
    if (paymentAmount > remainingAmount + 0.01) {
      toast.current.show({ severity: "error", summary: "Hata", detail: "Tahsilat tutarı kalan bakiyeyi aşamaz" });
      return;
    }

    if (paymentType === "SanalPos") {
      if (!selectedAccountId && !selectedSupplierId) {
        toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Sanal POS işleminde hesap veya tedarikçi seçilmelidir." });
        return;
      }
    } else if (paymentType === "Nakit" || paymentType === "Havale" || paymentType === "EFT" || paymentType === "KrediKarti") {
      if (!selectedAccountId) {
        toast.current.show({ severity: "warn", summary: "Uyarı", detail: "İşlem yapılacak hesap seçilmelidir." });
        return;
      }
    }

    setPayments((prev) => [
      ...prev,
      {
        id: Date.now(),
        amount: paymentAmount,
        method: paymentType,
        financialAccountId: selectedAccountId,
        supplierId: paymentType === "SanalPos" ? selectedSupplierId : null,
        note: paymentNote,
        date: new Date(),
        ...(paymentType === "Çek" ? chequeDetails : {}),
        ...(paymentType === "Senet" ? promissoryDetails : {}),
      },
    ]);
    setPaymentAmount(null);
    setPaymentNote("");
    setIsDirty(true);
  };

  const removePayment = (id) => {
    setPayments((prev) => prev.filter((p) => p.id !== id));
    setIsDirty(true);
  };

  // Save Logic
  const handleSave = async () => {
    if (!lines.length) {
      toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Satışta en az 1 ürün olmalıdır." });
      return false;
    }

    // Validation: Cash sale check (Peşin)
    if (receivableType === 1 && remainingAmount > 0.01) {
      toast.current.show({
        severity: "warn",
        summary: "Ödeme Eksik",
        detail: "Peşin seçiminde veya perakende satışlarda ödeme eklenmeden işlem kaydedilemez."
      });
      return false;
    }

    // Validation: Deferred sale check (Vadeli)
    if (receivableType === 3 && !dueDate) {
      toast.current.show({
        severity: "warn",
        summary: "Vade Tarihi Eksik",
        detail: "Vadeli satışlarda vade tarihi seçilmelidir."
      });
      return false;
    }

    const payload = {
      customerId: selectedCustomer?.id === 0 ? null : (selectedCustomer?.id || 0),
      supplierId: selectedCustomer?.supplierId || null,
      saleNote: salesHeader.description || "",
      discountAmount: generalDiscount,
      saleDate: formatDateOnly(salesHeader.salesDate),
      CustomerReceivableType: receivableType,
      DueDate: formatDateOnly(dueDate),
      productLines: lines.map((l) => ({
        id: 0,
        productId: l.productId,
        supplierId: l.supplierId || 0,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        discountAmount: 0,
        vatRate: l.vatRate || 0,
        note: l.note,
        warehouseId: l.warehouseId,
        dropFromStock: l.dropFromStock !== false,
        priceTierId: l.priceTierId
      })),
      payments: payments.map((p) => ({
        id: 0,
        method: p.method,
        amount: p.amount,
        financialAccountId: p.financialAccountId,
        supplierId: p.supplierId,
        description: p.note || "",
        bankName: p.bankName,
        bankBranch: p.bankBranch,
        checkNumber: p.checkNumber,
        dueDate: p.dueDate,
        drawer: p.drawer,
        guarantor: p.guarantor
      })),
    };

    try {
      setSaving(true);
      let res;
      const targetId = editId || lastSaleId;

      if (targetId) {
        res = await SaleService.updateSale(targetId, payload);
      } else {
        res = await SaleService.createSale(payload);
      }

      if (res.success === false) {
        toast.current.show({ severity: "error", summary: "Hata", detail: res.message });
        return false;
      } else {
        setIsDirty(false);

        if (res.message && res.message.includes("genel stok")) {
          toast.current.show({
            severity: "info",
            summary: "Bilgi",
            detail: res.message,
            life: 5000
          });
        } else {
          toast.current.show({
            severity: "success",
            summary: "Başarılı",
            detail: targetId ? "Satış başarıyla güncellendi." : "Satış başarıyla kaydedildi."
          });
        }

        if (selectedCustomer?.id) {
          customerService.getById(selectedCustomer.id).then(custRes => {
            if (custRes.data?.success && custRes.data?.data) {
              setSelectedCustomer(prev => ({
                ...prev,
                balance: custRes.data.data.balance
              }));
            }
          }).catch(custErr => console.error("Müşteri güncel bakiyesi alınamadı", custErr));
        }

        if (editId) {
          setTimeout(() => navigate(`/customers/detail/${selectedCustomer.id}`), 1500);
        } else if (!lastSaleId) {
          setLastSaleId(res.data || res);
        }
        return true;
      }
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Hata", detail: err.message || "Hata oluştu" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Fast Create Product logic
  const openCreateProductModal = (name) => {
    setCreateProductModal({
      visible: true,
      productName: name ?? "",
      unit: 1,
      unitType: "Adet",
      salePrice: 0,
    });
    setModalErrors({});
  };

  const closeCreateProductModal = (shouldRestoreFocus = true) => {
    setCreateProductModal((p) => ({ ...p, visible: false }));
    if (shouldRestoreFocus) setTimeout(() => productInputRef.current?.focus(), 0);
  };

  const handleCreateProductSubmit = async () => {
    const errs = {};
    if (!createProductModal.productName?.trim()) errs.productName = "Ürün adı zorunlu";
    if (!createProductModal.unit || createProductModal.unit <= 0) errs.unit = "Birim > 0 olmalı";
    if (createProductModal.salePrice < 0) errs.salePrice = "Fiyat negatif olamaz";

    if (Object.keys(errs).length > 0) {
      setModalErrors(errs);
      return;
    }

    const dto = {
      name: createProductModal.productName.trim(),
      unitType: createProductModal.unitType,
      unit: createProductModal.unit?.toString(),
      salePrice: Number(createProductModal.salePrice || 0),
    };

    try {
      const res = await SupplierService.fastCreateProduct({ fastProduct: dto });
      if (res.success) {
        toast.current.show({ severity: "success", summary: "Başarılı", detail: "Ürün oluşturuldu" });
        const data = res.data || res;
        const newProduct = {
          id: data.id || Date.now(),
          name: data.name || dto.name,
          unit: ["Mililitre", "Gram", "mg", "ml", "gr"].includes(data.unitType || dto.unitType) ? "Adet" : (data.unitType || dto.unitType || "Adet"),
          salePrice: dto.salePrice,
          vatRate: data.vatRate || 0,
        };
        selectProduct(newProduct);
        closeCreateProductModal(false);
      } else {
        toast.current.show({ severity: "error", summary: "Hata", detail: res.message || "Hata" });
      }
    } catch (error) {
      toast.current.show({ severity: "error", summary: "Hata", detail: "Hata oluştu" });
    }
  };

  const openDeleteConfirm = (event, id) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Bu satırı silmek istediğinize emin misiniz?",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sil",
      rejectLabel: "İptal",
      acceptClassName: "p-button-danger",
      accept: () => removeLine(id),
    });
  };

  const handleNewSale = () => {
    setLines([]);
    setPayments([]);
    setSelectedCustomer(null);
    setCustomerQuery("");
    setSalesHeader({ salesDate: new Date(), description: "" });
    setReceivableType(1);
    setDueDate(null);
    setLastSaleId(null);
    setGeneralDiscount(0);
    setTimeout(() => customerInputRef.current?.focus(), 100);
  };

  const lineTotalTemplate = (row) => {
    const total = (row.quantity * row.unitPrice);
    return (
      <span className="font-black text-slate-800 dark:text-slate-200">
        {total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
      </span>
    );
  };

  const numberEditor = (options) => {
    return (
      <InputNumber
        value={options.value}
        onValueChange={(e) => options.editorCallback(e.value)}
        className="w-full"
        mode={options.field === "quantity" ? "decimal" : "currency"}
        currency="TRY"
        locale="tr-TR"
        inputClassName="dark:bg-slate-900 dark:text-white dark:border-slate-700"
      />
    );
  };

  const noteEditor = (options) => {
    return (
      <InputTextarea
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}
        rows={3}
        autoResize
        className="w-full dark:bg-slate-900 dark:text-white dark:border-slate-700"
        placeholder="Satır notu..."
      />
    );
  };

  const noteBodyTemplate = (rowData) => {
    const note = (rowData.note || "").trim();
    const hasNote = note.length > 0;
    const shortNote = note.length > 24 ? note.substring(0, 24) + "…" : note;
    const tooltipClass = `note-tooltip-${rowData.id.toString().replace(/\./g, '-')}`;

    return (
      <>
        <Tooltip target={`.${tooltipClass}`} />
        <div
          className={`flex align-items-center gap-2 cursor-pointer ${tooltipClass}`}
          data-pr-tooltip={hasNote ? note : "Not ekle"}
          data-pr-position="top"
        >
          <i className={`pi pi-file-edit ${hasNote ? "text-emerald-500" : "text-slate-400 dark:text-slate-500"}`} />
          {hasNote && <span className="text-[10px] text-slate-600 dark:text-slate-300 font-medium">{shortNote}</span>}
        </div>
      </>
    );
  };

  return (
    <div className="   animate-fade-in bg-[#f8fafc] dark:bg-slate-950 min-h-screen">
      <style>{`
  /* Dark mode overrides for Product Sales Page */
  .dark .bg-white {
    background-color: #1e293b !important;
    border-color: #334155 !important;
  }

  .dark .border-slate-200,
  .dark .border-slate-100 {
    border-color: #334155 !important;
  }

  .dark .bg-slate-50,
  .dark .bg-slate-100,
  .dark .bg-slate-50\\/50 {
    background-color: #0f172a !important;
    border-color: #334155 !important;
  }

  .dark .p-dialog .p-dialog-header,
  .dark .p-dialog .p-dialog-content,
  .dark .p-dialog .p-dialog-footer {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-title {
    color: #ffffff !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-header-icon {
    color: #cbd5e1 !important;
  }

  .dark .p-dialog .p-dialog-header .p-dialog-header-icon:hover {
    background: #334155 !important;
    color: #ffffff !important;
  }

  .dark .p-datatable .p-datatable-thead > tr > th {
    background: #0f172a !important;
    color: #94a3b8 !important;
    border-color: #334155 !important;
  }

  .dark .p-datatable .p-datatable-tbody > tr {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .dark .p-datatable .p-datatable-tbody > tr:hover {
    background: #334155 !important;
  }

  .dark .p-datatable .p-datatable-emptymessage td {
    background: #1e293b !important;
    border-color: #334155 !important;
  }

  .dark .p-dropdown,
  .dark .p-dropdown-panel {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #cbd5e1 !important;
  }

  .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
    color: #cbd5e1 !important;
  }

  .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover {
    background: #334155 !important;
    color: #ffffff !important;
  }

  .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
    background: #3b82f6 !important;
    color: #ffffff !important;
  }

  .dark .p-autocomplete-panel {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .dark .p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item {
    color: #cbd5e1 !important;
  }

  .dark .p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item:hover {
    background: #334155 !important;
    color: #ffffff !important;
  }
`}</style>
      <div className="no-print">
        <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
        <ConfirmPopup />

        <Dialog
          header="Değişiklikleriniz kaydedilsin mi?"
          visible={blocker.state === "blocked"}
          style={{ width: '450px' }}
          modal
          onHide={() => blocker.reset()}
          footer={
            <div className="flex justify-end gap-2">
              <Button label="Vazgeç" icon="pi pi-times" className="p-button-text" onClick={() => blocker.reset()} />
              <Button label="Hayır" icon="pi pi-trash" className="p-button-danger p-button-text" onClick={() => blocker.proceed()} />
              <Button label="Evet" icon="pi pi-check" className="!bg-emerald-600 border-none" autoFocus onClick={async () => {
                const success = await handleSave();
                if (success) {
                  blocker.proceed();
                }
              }} />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle text-yellow-500 text-4xl" />
            <p className="m-0">
              Sayfadan ayrılmadan önce yaptığınız değişiklikleri kaydetmek ister misiniz?
            </p>
          </div>
        </Dialog>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 md:p-4 shadow-lg shadow-slate-400 dark:shadow-none">
          {/* Header */}
          <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-6">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <div className="!bg-emerald-600 p-3 rounded-xl shadow-md shadow-emerald-500/20">
                  <i className="pi pi-shopping-cart text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Satış Yap</h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 text-[9px] font-black rounded-lg uppercase border border-emerald-100 dark:border-emerald-900/30">YENİ SATIŞ</span>
                    <span className="text-slate-400 dark:text-slate-500 text-xs font-medium">• Ürün satışı ve tahsilat kaydı</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center md:justify-end gap-4">
              <div className="flex items-center gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-inner">
                {salesHeader.saleNo && (
                  <>
                    <div className="flex flex-col">
                      <span className="text-[9px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-widest mb-0.5">Fatura No</span>
                      <span className="font-black text-blue-600 dark:text-blue-400 tracking-tight leading-none text-sm">{salesHeader.saleNo}</span>
                    </div>
                    <div className="w-px h-6 bg-slate-200 dark:bg-slate-700"></div>
                  </>
                )}
                <div className="flex flex-col">
                  <span className="text-[9px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-widest mb-0.5">Müşteri / Cari</span>
                  {selectedCustomer ? (
                    <a
                      href={`/customers/detail/${selectedCustomer.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-black text-blue-600 dark:text-blue-400 hover:text-blue-800 underline tracking-tight leading-none text-sm cursor-pointer"
                    >
                      {selectedCustomer.name}
                    </a>
                  ) : (
                    <span className="font-black text-slate-800 dark:text-slate-200 tracking-tight leading-none text-sm">
                      Perakende Satış
                    </span>
                  )}
                </div>
                {selectedCustomer && (
                  <>
                    <div className="w-px h-6 bg-slate-200 dark:bg-slate-700"></div>
                    <div className="flex flex-col">
                      <span className="text-[9px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-widest mb-0.5">Cari Bakiye</span>
                      <span className={`font-black text-base ${selectedCustomer.balance >= 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {selectedCustomer.balance?.toLocaleString("tr-TR")} ₺
                      </span>
                    </div>
                  </>
                )}
              </div>
              {!lastSaleId ? (
                <Button
                  label="KAYDET (F2)"
                  icon="pi pi-save"
                  className="!bg-emerald-600 border-none hover:!bg-emerald-700 rounded-xl px-8 py-3.5 font-black text-sm shadow-lg !shadow-emerald-600/20 transition-all hover:scale-[1.02]"
                  onClick={handleSave}
                  loading={saving}
                  disabled={lines.length === 0}
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button
                    label="Tahsilat Yazdır"
                    icon="pi pi-print"
                    className="p-button-outlined p-button-info rounded-xl font-bold text-xs"
                    onClick={() => receiptRef.current?.print(lastSaleId, 'collection')}
                  />
                  <Button
                    label="Bilgi Fişi Yazdır"
                    icon="pi pi-print"
                    className="p-button-outlined p-button-success rounded-xl font-bold text-xs"
                    onClick={() => receiptRef.current?.print(lastSaleId, 'info')}
                  />
                  <Button
                    label="Detaylı Bilgi Fişi Yazdır"
                    icon="pi pi-print"
                    className="p-button-outlined p-button-warning rounded-xl font-bold text-xs"
                    onClick={() => receiptRef.current?.print(lastSaleId, 'detailed')}
                  />
                  <Button
                    label="Değişiklikleri Güncelle"
                    icon="pi pi-save"
                    loading={saving}
                    className="p-button-outlined p-button-secondary rounded-xl font-bold text-xs"
                    onClick={handleSave}
                  />
                  <Button
                    label="Yeni Satış Gir"
                    icon="pi pi-plus"
                    className="!bg-blue-600 border-none hover:!bg-blue-700 rounded-xl px-4 py-2 font-black text-sm shadow-lg"
                    onClick={handleNewSale}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[repeat(24,minmax(0,1fr))] gap-4 items-start">
            {/* Left Column */}
            <div className="lg:col-span-4">
              <div className="!bg-slate-100 dark:!bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-4">
                <h2 className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2 mb-2">
                  <i className="pi pi-user text-emerald-600"></i> Müşteri Bilgileri
                </h2>

                <div className="space-y-3.5">
                  <div className="space-y-1 relative">
                    <InputText
                      ref={customerInputRef}
                      value={customerQuery}
                      placeholder="Müşteri ara..."
                      className="w-full border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white rounded-xl focus:border-emerald-500 py-2.5 shadow-sm font-bold text-sm"
                      onChange={(e) => {
                        setCustomerQuery(e.target.value);
                        if (e.target.value.length >= 1) searchCustomers(e.target.value);
                        else { setCustomers([]); setShowCustomerList(false); }
                        if (selectedCustomer && e.target.value !== selectedCustomer.name) setSelectedCustomer(null);
                      }}
                      onFocus={() => { if (customerQuery.length >= 1 && customers.length) setShowCustomerList(true); }}
                      onKeyDown={(e) => {
                        if (e.key === "Tab") {
                          e.preventDefault();
                          productInputRef.current?.focus();
                          return;
                        }
                        if (!customers.length) return;
                        if (e.key === "ArrowDown") setCustomerHighlightIndex(prev => Math.min(prev + 1, customers.length - 1));
                        if (e.key === "ArrowUp") setCustomerHighlightIndex(prev => Math.max(prev - 1, 0));
                        if (e.key === "Enter") { e.preventDefault(); selectCustomer(customers[customerHighlightIndex]); }
                      }}
                    />
                    {showCustomerList && (
                      <div className="absolute top-[100%] left-0 right-0 mt-2 bg-white dark:bg-slate-800 shadow-2xl rounded-xl border border-slate-100 dark:border-slate-700 max-h-[200px] overflow-y-auto z-[100]">
                        {customers.map((c, i) => (
                          <div
                            key={c.id}
                            onMouseDown={() => selectCustomer(c)}
                            className={`px-4 py-3 cursor-pointer text-xs font-bold ${i === customerHighlightIndex ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-l-4 border-emerald-500" : "hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"}`}
                          >
                            <div className="flex flex-col">
                              <span className="font-bold">{c.name}</span>
                              {c.note && <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium italic">"{c.note}"</span>}
                              {c.phone && <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">{c.phone}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Satış Tarihi</label>
                    <Calendar
                      value={salesHeader.salesDate}
                      onChange={(e) => updateSalesHeader(prev => ({ ...prev, salesDate: e.value }))}
                      showIcon appendTo={document.body} dateFormat="dd.mm.yy"
                      locale="tr"
                      className="w-full"
                      inputClassName="rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white py-2.5 font-bold text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Açıklama</label>
                    <InputText
                      value={salesHeader.description}
                      onChange={(e) => updateSalesHeader(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Opsiyonel notlar..."
                      className="w-full border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white rounded-xl py-2.5 font-bold text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Alacak Tipi</label>
                    <Dropdown
                      value={receivableType}
                      options={receivableTypeOptions}
                      onChange={(e) => {
                        setReceivableType(e.value);
                        setIsDirty(true);
                      }}
                      disabled={!selectedCustomer}
                      className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 font-bold text-sm"
                    />
                  </div>

                  {receivableType === 3 && (
                    <div className="space-y-1.5 animate-in fade-in duration-300">
                      <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Vade Tarihi</label>
                      <Calendar
                        value={dueDate}
                        onChange={(e) => {
                          setDueDate(e.value);
                          setIsDirty(true);
                        }}
                        showIcon appendTo={mounted ? document.body : "self"} dateFormat="dd.mm.yy"
                        locale="tr"
                        minDate={new Date()}
                        className="w-full"
                        inputClassName="rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white py-2.5 font-bold text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* İSKONTO BÖLÜMÜ */}
              <div className="bg-orange-50/50 dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/30 rounded-2xl p-4 space-y-3 shadow-sm">
                <h2 className="text-[10px] font-black text-orange-700 dark:text-orange-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <i className="pi pi-percentage"></i> İSKONTO / İNDİRİM
                </h2>
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Genel İskonto / İndirim</label>
                  <InputNumber
                    value={generalDiscount}
                    onValueChange={(e) => updateGeneralDiscount(e.value || 0)}
                    placeholder="İndirim tutarı..."
                    mode="currency"
                    currency="TRY"
                    locale="tr-TR"
                    className="w-full"
                    inputClassName="w-full rounded-xl border-orange-200 dark:border-orange-900/40 focus:border-orange-500 py-2.5 font-black text-sm text-orange-600 dark:text-orange-400 shadow-inner bg-white/50 dark:bg-slate-900"
                  />
                </div>
              </div>
              {payments.length > 0 && (
                <div className="!bg-slate-100 dark:!bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-4">
                  <h2 className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2 mb-2">
                    <i className="pi pi-history text-emerald-600"></i> ÖDEME GEÇMİŞİ
                  </h2>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                    {payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-2xl border border-slate-100 dark:border-slate-700 group hover:border-emerald-500/30 transition-all duration-300 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-900 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                            <i className={`pi ${p.method === 'Nakit' ? 'pi-money-bill' :
                              p.method === 'KrediKarti' ? 'pi-credit-card' :
                              p.method === 'SanalPos' ? 'pi-globe' :
                                'pi-wallet'
                              } text-sm`}></i>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs font-black text-slate-700 dark:text-slate-300">
                              {p.method === 'KrediKarti' ? 'Kredi Kartı' : p.method === 'SanalPos' ? 'Sanal POS' : p.method}
                            </span>
                            {p.method === 'SanalPos' && p.supplierId && (
                              <span className="text-[10px] text-slate-400 font-bold leading-none mt-0.5">
                                Tedarikçi: {suppliers.find(s => s.id === p.supplierId)?.name || "Yükleniyor..."}
                              </span>
                            )}
                            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                              {p.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                            </span>
                          </div>
                        </div>
                        <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-0 w-8 h-8 transition-all" onClick={() => removePayment(p.id)} />
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-center text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                      <span>TOPLAM TAHSİLAT</span>
                      <span className="text-emerald-600 dark:text-emerald-400 text-sm">{paidTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Middle Column */}
            <div className="w-full" style={{ gridColumn: "span 15 / span 15" }}>
              <div className="bg-white dark:bg-slate-900 border border-slate-400  dark:border-slate-800 rounded-2xl p-2 space-y-6 shadow-sm">
                <h2 className="text-[12px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] flex items-center gap-2">
                  <i className="pi pi-box text-emerald-600"></i> ÜRÜN VE FİYAT GİRİŞİ

                </h2>

                <div className="flex flex-col gap-4">
                  <div className="relative" style={{ zIndex: 10 }}>
                    <label className="text-[12px] text-slate-600 dark:text-slate-300 font-black uppercase tracking-wider block ml-1 mb-1.5">Ürün Arama</label>
                    <div className="relative">
                      <i className="pi pi-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10 text-sm"></i>
                      <InputText
                        ref={productInputRef}
                        value={productQuery}
                        placeholder="Ürün adı veya barkod..."
                        className="w-full !pl-10 !py-3 border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white rounded-xl focus:ring-2 focus:ring-emerald-500 shadow-sm font-black text-base"
                        onChange={(e) => {
                          setProductQuery(e.target.value);
                          if (e.target.value.length >= 1) searchProducts(e.target.value);
                          else { setProducts([]); setShowList(false); }
                        }}
                        onFocus={() => { if (productQuery.length >= 1 && products.length) setShowList(true); }}
                        onKeyDown={(e) => {
                          if (!products.length) return;
                          if (e.key === "ArrowDown") setHighlightIndex(prev => Math.min(prev + 1, products.length - 1));
                          if (e.key === "ArrowUp") setHighlightIndex(prev => Math.max(prev - 1, 0));
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const item = products[highlightIndex];
                            if (!item) return;
                            if (item.isCreate) openCreateProductModal(item.rawName);
                            else selectProduct(item);
                          }
                        }}
                      />
                    </div>
                    {showList && products.length > 0 && (
                      <div className="absolute top-[100%] left-0 right-0 mt-2 bg-white dark:bg-slate-800 shadow-2xl rounded-2xl border border-slate-100 dark:border-slate-700 max-h-[320px] overflow-y-auto z-[9999]">
                        {products.map((p, i) => (
                          <div
                            key={p.id}
                            onMouseDown={() => p.isCreate ? openCreateProductModal(productQuery) : selectProduct(p)}
                            className={`flex items-center gap-3 px-5 py-4 cursor-pointer transition-all ${i === highlightIndex ? "!bg-emerald-600 text-white translate-x-2" : "hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"}`}
                          >
                            <i className={`pi ${p.isCreate ? 'pi-plus-circle' : 'pi-box'} ${i === highlightIndex ? '!text-white' : '!text-emerald-500'}`}></i>
                            <div className="flex flex-col">
                              <span className="font-black text-sm">{p.name}</span>
                              {!p.isCreate && (
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] ${i === highlightIndex ? '!text-emerald-100' : 'text-slate-400 dark:text-slate-500'}`}>Fiyat: {p.salePrice?.toLocaleString()} ₺</span>
                                  <span className="text-[10px] opacity-50">|</span>
                                  <span className={`text-[10px] font-bold ${i === highlightIndex ? '!text-white' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                    Mevcut Stok: {p.stockQuantity?.toLocaleString()} {p.unit} {p.warehouseStockDetail}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-end gap-3 bg-slate-50/50 dark:bg-slate-800/40 p-3 rounded-xl border border-slate-100 dark:border-slate-700">

                    {/* Miktar */}
                    <div className="space-y-1 sm:w-[140px]">
                      <label className="text-[10px] text-slate-600 dark:text-slate-300 font-black uppercase tracking-wider block ml-1">
                        Miktar
                      </label>

                      <InputNumber
                        ref={quantityRef}
                        value={newLine.quantity}
                        onValueChange={(e) =>
                          setNewLine(prev => ({ ...prev, quantity: e.value || 0 }))
                        }
                        min={1}
                        className="w-full"
                        inputClassName="w-full rounded-lg border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white py-2 font-black text-center text-base"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            focusAndSelect(priceRef);
                          }
                        }}
                      />
                    </div>

                    {/* Birim Fiyat */}
                    <div className="space-y-1 sm:w-[180px]">
                      <label className="text-[10px] text-slate-600 dark:text-slate-300 font-black uppercase tracking-wider block ml-1">
                        Birim Fiyat
                      </label>

                      <InputNumber
                        ref={priceRef}
                        value={newLine.unitPrice}
                        onValueChange={(e) =>
                          setNewLine(prev => ({ ...prev, unitPrice: e.value || 0 }))
                        }
                        mode="currency"
                        currency="TRY"
                        locale="tr-TR"
                        className="w-full"
                        inputClassName="w-full rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white py-2 font-black text-center text-base"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            focusAndSelect(noteInputRef);
                          }
                        }}
                      />
                    </div>

                    {/* Satır Notu */}
                    <div className="space-y-1 flex-1 min-w-[240px]">
                      <label className="text-[10px] text-slate-600 dark:text-slate-300 font-black uppercase tracking-wider block ml-1">
                        Satır Notu
                      </label>

                      <InputText
                        ref={noteInputRef}
                        value={newLine.note}
                        placeholder="Not..."
                        className="w-full border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white rounded-xl py-2 font-medium text-sm"
                        onChange={(e) =>
                          setNewLine(prev => ({ ...prev, note: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addLineBtnRef.current?.focus();
                          }
                        }}
                      />
                    </div>

                    {/* Ekle Butonu */}
                    <div className="w-full sm:w-auto sm:ml-auto">
                      <Button
                        ref={addLineBtnRef}
                        label={isAddFocused ? "ENTER ile EKLE" : "EKLE"}
                        icon="pi pi-plus"
                        className="w-full sm:w-auto !bg-emerald-500 border-none hover:bg-emerald-600 rounded-xl px-4 py-3 font-black text-white shadow-lg transition-all"
                        onFocus={() => setIsAddFocused(true)}
                        onBlur={() => setIsAddFocused(false)}
                        onClick={handleAddLine}
                        disabled={!newLine.product}
                      />
                    </div>
                  </div>

                  {newLine.product && (
                    <div className="flex flex-wrap items-center gap-4 bg-slate-50/50 dark:bg-slate-800/40 p-3 rounded-xl border border-slate-100 dark:border-slate-700 animate-in fade-in slide-in-from-top-2 duration-300">

                      {/* Depo Seçimi - Sadece birden fazla depo varsa gösterilir */}
                      {newLine.product.warehouseStocks?.length > 1 && (
                        <div className="space-y-1 sm:w-[240px]">
                          <label className="text-[10px] text-slate-600 dark:text-slate-300 font-black uppercase tracking-wider block ml-1">
                            Depo Seçimi
                          </label>
                          <Dropdown
                            ref={warehouseRef}
                            value={newLine.warehouseId}
                            options={warehouses}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => {
                              setNewLine(prev => ({ ...prev, warehouseId: e.value }));
                              setTimeout(() => focusAndSelect(quantityRef), 100);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                if (newLine.warehouseId) {
                                  handleAddLine();
                                } else {
                                  warehouseRef.current?.show();
                                }
                              }
                            }}
                            placeholder="Depo seçin..."
                            className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 font-bold text-sm"
                            itemTemplate={(option) => {
                              const stock = newLine.product?.warehouseStocks?.find(ws => ws.warehouseId === option.id);
                              return (
                                <div className="flex justify-between items-center w-full">
                                  <span className="font-bold">{option.name}</span>
                                  {stock && <span className="text-[10px] bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded font-black">Stok: {stock.quantity}</span>}
                                </div>
                              )
                            }}
                          />
                        </div>
                      )}

                      {/* Stoktan Düş */}
                      <div className={`flex items-center gap-2 bg-white dark:bg-slate-800 px-3 py-2 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm whitespace-nowrap min-h-[42px] ${newLine.product.warehouseStocks?.length > 1 ? 'mt-5' : ''}`}>
                        <Checkbox
                          checked={!!newLine.dropFromStock}
                          onChange={(e) =>
                            setNewLine(prev => ({ ...prev, dropFromStock: e.checked }))
                          }
                          inputId="dropFromStockCheck"
                        />

                        <label
                          htmlFor="dropFromStockCheck"
                          className="text-[10px] font-black text-slate-600 dark:text-slate-300 uppercase cursor-pointer select-none"
                        >
                          Stoktan Düş
                        </label>
                      </div>

                      {/* Hızlı Bilgi - Tek depo varsa hangisi olduğunu kullanıcıya fısılda */}
                      {newLine.product.warehouseStocks?.length === 1 && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl">
                          <i className="pi pi-info-circle text-emerald-600 dark:text-emerald-400 text-xs"></i>
                          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase">
                            {newLine.product.warehouseStocks[0].warehouseName} deposundan düşülecek (Mevcut: {newLine.product.warehouseStocks[0].quantity})
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <DataTable
                  value={lines}
                  size="small"
                  className="mt-4"
                  editMode="cell"
                  dataKey="id"
                  rowClassName={() => 'hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors'}
                  emptyMessage={
                    <div className="flex flex-col items-center gap-2 py-6 text-slate-400 dark:text-slate-500">
                      <i className="pi pi-inbox text-3xl"></i>
                      <span className="font-bold text-sm">
                        Henüz ürün eklenmedi
                      </span>
                      <span className="text-xs">
                        Yukarıdan ürün seçerek satır ekleyebilirsin
                      </span>
                    </div>
                  }
                >
                  <Column
                    field="productName"
                    header="Ürün"
                    headerClassName="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase py-3 px-4"
                    className="font-bold text-slate-700 dark:text-slate-300"
                    body={(row) => {
                      const purchasePrice = row.product?.lastPurchasePrice || row.lastPurchasePrice;
                      return (
                        <div title={purchasePrice ? `Geliş Fiyatı: ${purchasePrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : undefined}>
                          <div className="flex flex-col">
                            <span>{row.productName}</span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">{row.warehouseName}</span>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Column
                    field="vatRate"
                    header="KDV"
                    className="text-center"
                    headerClassName="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase py-3 px-4"
                    body={(row) => <span className="font-medium text-slate-500 dark:text-slate-400">%{row.vatRate}</span>}
                  />
                  <Column
                    field="quantity"
                    header="Miktar"
                    className="text-center dark:text-slate-300"
                    headerClassName="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase py-3 px-4"
                    onCellEditComplete={onCellEditComplete}
                    editor={numberEditor}
                    body={(row) => <span className="font-medium">{row.quantity}{row.unit && isNaN(row.unit) ? ` ${row.unit}` : ""}</span>}
                  />
                  <Column
                    field="unitPrice"
                    header="Birim Fiyat"
                    className="text-center dark:text-slate-300"
                    headerClassName="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase py-3 px-4"
                    onCellEditComplete={onCellEditComplete}
                    editor={numberEditor}
                    body={(row) => <span>{row.unitPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>}
                  />
                  <Column header="Toplam" headerClassName="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase py-3 px-4" className="font-black text-slate-800 dark:text-slate-200 text-right" body={lineTotalTemplate} />
                  <Column
                    field="dropFromStock"
                    header="Stok"
                    headerClassName="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase py-3 px-4 text-center"
                    className="text-center"
                    body={(row) => (
                      <i className={`pi ${row.dropFromStock !== false ? 'pi-check-circle text-emerald-500' : 'pi-times-circle text-red-400'} text-lg`}></i>
                    )}
                    editor={(options) => (
                      <div className="flex justify-center">
                        <Checkbox
                          checked={options.value}
                          onChange={(e) => options.editorCallback(e.checked)}
                        />
                      </div>
                    )}
                    onCellEditComplete={onCellEditComplete}
                    style={{ width: "70px" }}
                  />
                  <Column
                    field="note"
                    header="Not"
                    headerClassName="bg-slate-50 dark:bg-slate-800 text-[10px] font-black uppercase py-3 px-4"
                    body={noteBodyTemplate}
                    editor={noteEditor}
                    onCellEditComplete={onCellEditComplete}
                    style={{ width: "150px" }}
                  />
                  <Column headerClassName="bg-slate-50 dark:bg-slate-800" bodyStyle={{ textAlign: "right" }} body={(row) => (
                    <div className="flex justify-end gap-1">
                      <Button icon="pi pi-minus" className="w-8 h-8 p-0 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg border-none" onClick={() => changeLineQuantity(row.id, -1)} />
                      <Button icon="pi pi-plus" className="w-8 h-8 p-0 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-lg border-none" onClick={() => changeLineQuantity(row.id, 1)} />
                      <Button icon="pi pi-trash" className="w-8 h-8 p-0 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg border-none" onClick={(e) => openDeleteConfirm(e, row.id)} />
                    </div>
                  )} />
                </DataTable>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-5 space-y-1">
              {/* TAHSİLAT BÖLÜMÜ */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-lg shadow-slate-400 dark:shadow-none">
                <div className="bg-emerald-700 p-4 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <i className="pi pi-wallet text-8xl rotate-12"></i>
                  </div>
                  <div className="relative z-10 flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-black uppercase text-emerald-50">Fatura Tutarı</span>
                      <Tag value={paymentStatus === "PAID" ? "TAMAMLANDI" : paymentStatus === "PARTIAL" ? "KISMİ" : "ÖDENMEDİ"}
                        severity={paymentStatus === "PAID" ? "success" : paymentStatus === "PARTIAL" ? "warning" : "danger"}
                        className="!bg-white/20 !text-white text-[9px] font-black px-2.5 py-1 rounded-full border border-white/20" />
                    </div>
                    <div className="text-2xl font-black tracking-tight whitespace-nowrap">
                      {remainingAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-lg font-medium opacity-80">₺</span>
                    </div>

                    <div className="flex gap-4 pt-2 border-t border-white/10">
                      <div className="flex-1 text-right">
                        <span className="text-[9px] font-black text-emerald-100/60 uppercase block mb-0.5">TAHSİLAT</span>
                        <span className="font-bold text-sm text-emerald-300">{paidTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-8">
                  <div className="space-y-4">
                    <div className="space-y-5">
                      <div className="space-y-4 px-1">
                        <div className="space-y-1.5">
                          <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Tahsilat Tutarı</label>
                          <InputNumber
                            value={paymentAmount}
                            onValueChange={(e) => setPaymentAmount(e.value)}
                            placeholder="0,00"
                            mode="currency"
                            currency="TRY"
                            locale="tr-TR"
                            className="w-full"
                            inputClassName="w-full rounded-xl border-slate-200 dark:border-slate-700 py-3 px-4 font-black text-lg text-slate-800 dark:text-white bg-slate-50/50 dark:bg-slate-950 focus:bg-white dark:focus:bg-slate-950 transition-all shadow-sm"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Ödeme Yöntemi</label>
                          <Dropdown
                            value={paymentType}
                            options={paymentTypeOptions}
                            onChange={(e) => setPaymentType(e.value)}
                            className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-950 font-bold"
                            itemTemplate={(option) => (
                              <div className="flex items-center gap-2 py-1">
                                <i className={`pi ${option.value === 'Nakit' ? 'pi-money-bill text-orange-500' :
                                  option.value === 'KrediKarti' ? 'pi-credit-card text-blue-500' :
                                    option.value === 'SanalPos' ? 'pi-globe text-purple-500' :
                                      'pi-wallet text-emerald-500'
                                  } text-sm`}></i>
                                <span className="font-bold text-sm">{option.label}</span>
                              </div>
                            )}
                          />
                        </div>

                        {(filteredAccounts.length > 0 || paymentType === "SanalPos") && (
                          <div className="space-y-1.5 animate-in fade-in duration-300">
                            <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">İşlem Yapılacak Hesap</label>
                            <Dropdown
                              value={selectedAccountId}
                              options={filteredAccounts}
                              optionLabel="name"
                              optionValue="id"
                              onChange={(e) => handleAccountChange(e.value)}
                              placeholder="Hesap seçin..."
                              filter
                              showClear
                              className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-950 font-bold"
                            />
                          </div>
                        )}

                        {paymentType === "SanalPos" && (
                          <div className="space-y-1.5 animate-in fade-in duration-300">
                            <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Tedarikçi</label>
                            <Dropdown
                              value={selectedSupplierId}
                              options={suppliers}
                              optionLabel="name"
                              optionValue="id"
                              onChange={(e) => handleSupplierChange(e.value)}
                              placeholder="Tedarikçi seçin..."
                              filter
                              showClear
                              className="w-full rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-950 font-bold"
                            />
                          </div>
                        )}

                        {paymentType === "Çek" && (
                          <div className="bg-emerald-50/30 dark:bg-emerald-950/20 p-4 rounded-2xl border border-emerald-100/50 dark:border-emerald-900/30 space-y-3 animate-in fade-in duration-300">
                            <InputText value={chequeDetails.bankName} onChange={e => setChequeDetails({ ...chequeDetails, bankName: e.target.value })} placeholder="Banka Adı" className="w-full rounded-xl border-emerald-100/50 dark:border-slate-700 py-2.5 font-bold text-sm bg-white dark:bg-slate-900 dark:text-white" />
                            <InputText value={chequeDetails.checkNumber} onChange={e => setChequeDetails({ ...chequeDetails, checkNumber: e.target.value })} placeholder="Çek Numarası" className="w-full rounded-xl border-emerald-100/50 dark:border-slate-700 py-2.5 font-bold text-sm bg-white dark:bg-slate-900 dark:text-white" />
                            <Calendar value={chequeDetails.dueDate} onChange={e => setChequeDetails({ ...chequeDetails, dueDate: e.value })} placeholder="Vade Tarihi" dateFormat="dd.mm.yy" locale="tr" showIcon className="w-full" inputClassName="rounded-xl border-emerald-100/50 dark:border-slate-700 py-2.5 font-bold text-sm bg-white dark:bg-slate-900 dark:text-white" appendTo={document.body} />
                            <InputText value={chequeDetails.drawer} onChange={e => setChequeDetails({ ...chequeDetails, drawer: e.target.value })} placeholder="Keşideci" className="w-full rounded-xl border-emerald-100/50 dark:border-slate-700 py-2.5 font-bold text-sm bg-white dark:bg-slate-900 dark:text-white" />
                          </div>
                        )}

                        {paymentType === "Senet" && (
                          <div className="bg-emerald-50/30 dark:bg-emerald-950/20 p-4 rounded-2xl border border-emerald-100/50 dark:border-emerald-900/30 space-y-3 animate-in fade-in duration-300">
                            <Calendar value={promissoryDetails.dueDate} onChange={e => setPromissoryDetails({ ...promissoryDetails, dueDate: e.value })} placeholder="Vade Tarihi" dateFormat="dd.mm.yy" locale="tr" showIcon className="w-full" inputClassName="rounded-xl border-emerald-100/50 dark:border-slate-700 py-2.5 font-bold text-sm bg-white dark:bg-slate-900 dark:text-white" appendTo={document.body} />
                            <InputText value={promissoryDetails.drawer} onChange={e => setPromissoryDetails({ ...promissoryDetails, drawer: e.target.value })} placeholder="Borçlu Ad Soyad" className="w-full rounded-xl border-emerald-100/50 dark:border-slate-700 py-2.5 font-bold text-sm bg-white dark:bg-slate-900 dark:text-white" />
                            <InputText value={promissoryDetails.guarantor} onChange={e => setPromissoryDetails({ ...promissoryDetails, guarantor: e.target.value })} placeholder="Kefil" className="w-full rounded-xl border-emerald-100/50 dark:border-slate-700 py-2.5 font-bold text-sm bg-white dark:bg-slate-900 dark:text-white" />
                          </div>
                        )}

                        <div className="space-y-1.5">
                          <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Açıklama</label>
                          <InputText
                            value={paymentNote}
                            onChange={(e) => setPaymentNote(e.target.value)}
                            placeholder="Tahsilat notu..."
                            className="w-full rounded-xl py-3 px-4 font-bold text-sm bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 dark:text-white"
                          />
                        </div>
                      </div>

                      <Button
                        label="TAHSİLAT EKLE"
                        icon="pi pi-plus-circle"
                        className="!bg-emerald-600 border-none w-full font-black text-xs py-4 rounded-2xl shadow-2xl shadow-emerald-600/30 hover:!bg-emerald-700 active:scale-[0.98] transition-all transform"
                        onClick={handleAddPayment}
                        disabled={remainingAmount <= 0 || !paymentAmount}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* New Customer Dialog */}
        <Dialog
          header="Yeni Müşteri Ekle"
          visible={showCustomerDialog}
          style={{ width: "30rem" }}
          modal
          onHide={() => setShowCustomerDialog(false)}
          footer={
            <div className="flex justify-end gap-2">
              <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => setShowCustomerDialog(false)} />
              <Button label="Kaydet" icon="pi pi-check" className="!bg-emerald-600 border-none p-button-sm rounded-xl px-4" onClick={() => setShowCustomerDialog(false)} />
            </div>
          }
        >
          <div className="flex flex-col gap-3 mt-2">
            <div><label className="text-xs text-slate-400 font-bold uppercase mb-1 block">Müşteri Adı</label><InputText value={newCustomer.name} onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))} className="w-full rounded-xl" /></div>
            <div><label className="text-xs text-slate-400 font-bold uppercase mb-1 block">Cari Kodu</label><InputText value={newCustomer.code} onChange={(e) => setNewCustomer(prev => ({ ...prev, code: e.target.value }))} className="w-full rounded-xl" /></div>
            <div><label className="text-xs text-slate-400 font-bold uppercase mb-1 block">Telefon</label><InputText value={newCustomer.phone} onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))} className="w-full rounded-xl" /></div>
          </div>
        </Dialog>
      </div>
      <SaleReceipt ref={receiptRef} />
      <ConfirmDialog />

      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 min-w-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <i className="pi pi-tag text-lg"></i>
            </div>
            <div className="flex flex-col">
              <h3 className="text-base font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none">Satış Fiyatı Belirle</h3>
              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest leading-none mt-1.5">Ürün Fiyat Ayarları</span>
            </div>
          </div>
        }
        visible={priceModal.visible}
        style={{ width: '450px' }}
        modal
        showHeader
        blockScroll
        className="price-setup-dialog"
        contentClassName="!p-0"
        onHide={() => setPriceModal({ ...priceModal, visible: false })}
        footer={
          <div className="flex justify-end gap-2 p-4 bg-slate-50/50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold text-xs" onClick={() => setPriceModal({ ...priceModal, visible: false })} />
            <Button label="Fiyatı Kaydet ve Ekle" icon="pi pi-check" className="!bg-emerald-600 border-none hover:!bg-emerald-700 rounded-xl px-5 py-2.5 font-black text-xs shadow-lg shadow-emerald-600/20 transition-all active:scale-[0.98]" onClick={handlePriceModalSave} />
          </div>
        }
      >
        <div className="flex flex-col gap-6 p-6">
          <div className="bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 p-4 rounded-2xl flex items-start gap-3">
            <i className="pi pi-info-circle text-blue-500 mt-0.5"></i>
            <p className="text-xs text-blue-700 dark:text-blue-400 font-medium leading-relaxed">
              <span className="font-black text-blue-900 dark:text-blue-200 uppercase">{priceModal.product?.name}</span> ürünü için henüz bir satış fiyatı tanımlanmamış. Satışa devam etmek için bir fiyat belirlemelisiniz.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5">
            <div className="space-y-2">
              <label className="text-[11px] text-slate-500 dark:text-slate-400 font-black uppercase tracking-wider block ml-1">Satış Fiyatı</label>
              <div className="relative group">
                <InputNumber
                  value={priceModal.salePrice}
                  onValueChange={(e) => setPriceModal(prev => ({ ...prev, salePrice: e.value }))}
                  mode="currency"
                  currency="TRY"
                  locale="tr-TR"
                  className="w-full"
                  inputClassName="w-full rounded-2xl border-slate-200 dark:border-slate-700 py-4 px-5 font-black text-xl text-slate-800 dark:text-white bg-white dark:bg-slate-900 group-hover:border-emerald-400 focus:border-emerald-500 transition-all shadow-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div className="flex flex-col">
                <span className="text-xs font-black text-slate-700 dark:text-slate-300">KDV Dahil Fiyat</span>
                <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold">Girilen tutara KDV dahil mi?</span>
              </div>
              <Checkbox
                inputId="priceModalVatIncluded"
                checked={priceModal.salePriceIsVatIncluded}
                onChange={(e) => setPriceModal(prev => ({ ...prev, salePriceIsVatIncluded: e.checked }))}
                className="scale-125"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[11px] text-slate-500 dark:text-slate-400 font-black uppercase tracking-wider block ml-1">KDV Oranı</label>
              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  {[0, 1, 10, 20].map(rate => (
                    <Button
                      key={rate}
                      label={`%${rate}`}
                      className={`flex-1 py-2.5 rounded-xl font-black text-xs transition-all ${priceModal.taxRate === rate ? '!bg-emerald-600 !text-white border-none shadow-md' : 'p-button-outlined p-button-secondary !text-slate-500 dark:!text-slate-400'}`}
                      onClick={() => setPriceModal(prev => ({ ...prev, taxRate: rate }))}
                    />
                  ))}
                </div>
                <div className="relative group">
                  <InputNumber
                    value={priceModal.taxRate}
                    onValueChange={(e) => setPriceModal(prev => ({ ...prev, taxRate: e.value }))}
                    suffix="%"
                    className="w-full"
                    inputClassName="w-full rounded-xl border-slate-200 dark:border-slate-700 py-3 px-4 font-black text-sm text-slate-700 dark:text-white bg-white dark:bg-slate-900 group-hover:border-emerald-400 focus:border-emerald-500 transition-all shadow-sm"
                    placeholder="Diğer KDV oranı girin..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
            <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
              <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">KDV HARİÇ</span>
              <span className="text-sm font-black text-slate-700 dark:text-slate-300">
                {(priceModal.salePriceIsVatIncluded ? (priceModal.salePrice / (1 + priceModal.taxRate / 100)) : priceModal.salePrice).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </span>
            </div>
            <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30">
              <span className="text-[9px] font-black text-emerald-600/60 dark:text-emerald-400/60 uppercase tracking-widest block mb-1">KDV DAHİL</span>
              <span className="text-sm font-black text-emerald-700 dark:text-emerald-300">
                {(priceModal.salePriceIsVatIncluded ? priceModal.salePrice : (priceModal.salePrice * (1 + priceModal.taxRate / 100))).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </span>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
